import { useId } from 'react'

import { cx } from '../atoms/cx.ts'

import { TRAY_CELL_H, TRAY_CELL_W } from './TrayFigure.tsx'

import './figures.css'

/**
 * 챔버 한 개 — **시료 탭이 쓰는 단독 챔버 그림.**
 *
 * eric 지시(2026-09-21): 「챔버를 그려줬으면 좋겠어. **시료는 필요없고**.. 챔버 그려줘 …
 * **그림을 챔버로** 시료를 표현해줘.. 환자당 2개의 챔버겠지?」
 *
 * 🔴 **평면(top-down)이다 — 입체를 걷었다**(2026-09-22 · eric 「지금 3d 챔버 표현한게 아직
 *    매끄럽지 않아」 · 「이거 꼭 챔버가 아니어도 돼」). 종전에는 사선 투영 상자(윗면·앞면·옆면)
 *    였고, 그 투영 하나가 결함 여럿의 원인이었다 —
 *    ⓐ 윗면은 기울었는데 표지 `●` 는 정원이고 글자는 수평이라 **같은 면 위에서 투영이 갈렸다**
 *    ⓑ 윗면 테와 앞면이 둘 다 순검정이라 **두께가 안 읽혔다**
 *    ⓒ 투영이 낳은 소수 좌표라 선이 반 픽셀에 걸려 **흐렸다**
 *    ⓓ 앞면이 그림 잉크의 큰 몫을 먹는데 **정보가 0** 이었다
 *    ⇒ 랩웨어를 그리는 업계 도구(Opentrons · PyLabRobot)가 전부 평면 top-down 이다.
 *      **정수 좌표 + `rx` 둥근 모서리**로 다시 세웠다. 사선 `polygon` 을 되살리지 마라.
 *
 * 🔴 **2026-09-23 — 이 그림 위에 실제 3D 가 얹혔다. 위 판정은 그대로 참이다.**
 *    eric 지시: 「일단 시료의 챔버만 … 하이픽 챔버야. CTCells 에서 만든 거」 ⇒ **시료 탭**의
 *    챔버는 three.js 로 그린 HypICC 챔버 실물(`figures/chamber3d/`)이 그린다.
 *    ⚠ 위 ⓐ~ⓓ 는 **SVG 사선 투영**(손으로 계산한 가짜 입체)에 대한 판정이고, 3D 는 그 넷을
 *      다르게 푼다 — 투영은 래스터라이저가 하고, 표지는 도형 자체가 지고, 글자는 **그림 밖
 *      DOM**에 둔다. **이 파일에 사선 `polygon` 을 되살리지 마라**는 그대로다.
 *    🔴 **이 그림은 콘솔 스킨이 아닌 앱(장비 관점 콘솔 · 모듈 패드)의 챔버다.** 지우지 마라.
 *      통합 콘솔의 챔버(3D 는 2026-10-01 회의로 걷혔다 · 늘 2D)는 2026-09-29 부터 **하이픽 챔버를 바로 위에서 본 그림**
 *      (`ChamberStageFigure`)이 선다(eric 「하이픽 챔버 느낌 나게」 · 「위에서 본걸로 만들어 달라니까」).
 *      그 그림도 사선 투영이 없는 평면 top-down 이다(`chamberOutline.ts` 머리말) — 이 파일의 판정과 같은 쪽이다.
 *      트레이 그림(`TrayFigure`)은 칸 크기를 이 도형과 나눠 쓴다. 통합 콘솔 장비 탭은 2026-09-27 부터 트레이
 *      칸에 `ChamberView`(3D · 폴백이 이 그림)를 세운다(`TrayFigure` 의 `chamber`).
 *
 * 🔴 **트레이 배치도가 아니다.** 챔버가 트레이 «어느 칸»에 있는지는 우리가 모른다(BR-31).
 *    여기 그리는 것은 **챔버 그 자체**이고 이름은 레시피가 적은 웰 이름 원문이다.
 * 🔴 **색만으로 가르지 않는다.** 상태는 **축 넷**이 같이 말한다 —
 *    ⓐ **채움 농도** — 빈 · 절반(진행 중) · 꽉(지남) · 대각 해칭(실패)
 *    ⓑ **테** — 실선(지남) · 굵은 실선(지금) · 점선(아직 없음 · 배정 모름)
 *    ⓒ **표지 도형** — `flowVocabulary.ts` 의 글리프 표(`● ◐ ○ ▲ — ·`)와 같은 뜻
 *    ⓓ **글자** — 그림 자체는 `aria-hidden` 이고 부르는 쪽이 문구를 붙인다
 *    색(강조 1 · 위험 1)은 그 위에 **얹을 뿐**이라, 색이 없는 앱(장비 관점 · 모듈 패드)에서도
 *    네 축이 그대로 선다. 상태별 색을 늘리지 마라.
 * ⚠ 모션은 토큰이 쥔다(`--dur-pulse`) — `prefers-reduced-motion` 에서 0ms 로 떨어진다.
 */

/** 🔴 **흐름 어휘와 같은 집합이다**(`flowStateKey()` 가 내는 값 그대로). 새 어휘를 만들지 않는다 */
export type ChamberFigureState = 'marked' | 'active' | 'pending' | 'halted' | 'fail' | 'unassigned'

export interface ChamberFigureProps {
  /** 🔴 **레시피가 적은 웰 이름 그대로**(`A1`·`C3`). 「챔버 1」 같은 번호를 지어내지 않는다 */
  well: string
  /** 상태. `flowStateKey()` 의 결과를 그대로 넘긴다 */
  state: ChamberFigureState
}

const W = TRAY_CELL_W
const H = TRAY_CELL_H

/*
 * ── 평면 기하 ────────────────────────────────────────────────────────────
 * 🔴 **좌표가 전부 정수다.** 사선 투영이 낳던 소수(`x + (38−y)·0.3125`)가 선을 반 픽셀에
 *    걸쳐 흐리게 만들었다. 정수면 1px 테가 또렷하다.
 * 🔴 **viewBox 는 그대로 72×50** — 카드의 챔버 자리(`.bundle__chambers` 의 `--chamber-w`)가
 *    이 비율로 잡혀 있다.
 *
 * ```
 *  ┌───────────────────────────┐   몸체(rx=6) — 테가 상태를 말한다
 *  │ ┌───────────────────────┐ │   안쪽 면(rx=4) — 톤 차이로 오목해 보인다
 *  │ │  ╭──╮                 │ │
 *  │ │  │◐ │      A1         │ │   cavity(원) — 채움 농도가 상태를 말한다
 *  │ │  ╰──╯                 │ │
 *  │ └───────────────────────┘ │
 *  └───────────────────────────┘
 * ```
 */
/** 몸체 — 사방 1 을 띄워 테가 잘리지 않게 */
const BODY = { x: 1, y: 1, w: W - 2, h: H - 2, r: 6 } as const
/** 안쪽 면 — 몸체에서 4 를 더 들인다 */
const INNER = { x: 5, y: 5, w: W - 10, h: H - 10, r: 4 } as const
/** 챔버 구멍 — 안쪽 면 왼쪽. 🔴 **이것이 채움 농도를 지는 도형**이다 */
const CAVITY = { cx: 20, cy: 25, r: 9 } as const
/** 웰 이름 — 구멍 오른쪽 남은 폭의 가운데 */
const LABEL_X = 46
const LABEL_Y = 29

/** 해칭 한 칸의 크기 — 실패 전용. 값이 작을수록 촘촘하다 */
const HATCH = 4

/**
 * 상태 표지 — **글리프를 글자로 찍지 않고 도형으로 그린다.**
 * 🔴 `flowVocabulary.ts` 의 글리프 표(`● ◐ ○ ▲ — ·`)와 **같은 뜻**을 같은 순서로 그린 것이다.
 *    한쪽만 고치면 카드 안에서 두 말이 된다.
 * 🔴 **채움 농도가 곧 이 도형이다** — 빈 테(대기) · 왼쪽 절반(진행 중) · 꽉(지남) ·
 *    해칭(실패) · 가로 막대(중단) · 작은 점(배정 모름).
 */
function ChamberMark({ state, hatchId }: { state: ChamberFigureState; hatchId: string }) {
  const { cx: x, cy: y, r } = CAVITY
  switch (state) {
    /* ● 지남 — 꽉 찬 구멍 */
    case 'marked':
      return <circle className="fig-ch__ink" cx={x} cy={y} r={r} />
    /* ◐ 진행 중 — 테 + 왼쪽 반 채움(맥동은 CSS 가 건다) */
    case 'active':
      return (
        <>
          <circle className="fig-ch__ring" cx={x} cy={y} r={r} />
          <path
            className="fig-ch__ink"
            d={`M${String(x)} ${String(y - r)} A${String(r)} ${String(r)} 0 0 0 ${String(x)} ${String(y + r)} Z`}
          />
        </>
      )
    /*
     * ▲ 실패 — **대각 해칭.** 🔴 해칭은 **실패 전용**이다(축 ⓓ). 다른 상태에 쓰면
     *    「채움 농도」축이 두 말을 하게 된다.
     */
    case 'fail':
      return (
        <>
          <circle className="fig-ch__hatch" cx={x} cy={y} fill={`url(#${hatchId})`} r={r} />
          <circle className="fig-ch__ring fig-ch__ring--fail" cx={x} cy={y} r={r} />
        </>
      )
    /* — 중단 · 폐기 — 가로 막대 */
    case 'halted':
      return <rect className="fig-ch__ink" height={4} rx={2} width={r * 2} x={x - r} y={y - 2} />
    /* · 배정 모름 — 작은 점. 🔴 「대기」(빈 테)와 **다른 도형**이어야 한다 */
    case 'unassigned':
      return <circle className="fig-ch__ink" cx={x} cy={y} r={3} />
    /* ○ 대기 — 빈 테 */
    default:
      return <circle className="fig-ch__ring" cx={x} cy={y} r={r} />
  }
}

/**
 * 🔴 **그림은 `aria-hidden` 이다.** 스크린리더가 읽는 것은 부르는 쪽이 붙이는 문구다
 *    (「챔버 A1 · 원심 진행 중」) — 그림이 따로 말하면 한 챔버가 두 번 읽힌다.
 */
export function ChamberFigure({ well, state }: ChamberFigureProps) {
  /*
   * 🔴 **해칭 무늬 id 를 인스턴스마다 다르게 둔다.** 한 화면에 챔버가 열 개씩 서는데
   *    같은 id 를 박으면 문서에 중복 id 가 쌓이고, 무늬 색을 상태별로 달리 줄 때 첫 번째
   *    정의가 전부를 덮는다.
   */
  const hatchId = useId()

  return (
    <svg
      aria-hidden="true"
      className={cx('fig', 'fig-ch', `fig-ch--${state}`)}
      focusable="false"
      viewBox={`0 0 ${String(W)} ${String(H)}`}
    >
      <defs>
        <pattern
          height={HATCH}
          id={hatchId}
          patternTransform="rotate(45)"
          patternUnits="userSpaceOnUse"
          width={HATCH}
        >
          <line className="fig-ch__hatchline" x1={0} x2={0} y1={0} y2={HATCH} />
        </pattern>
      </defs>
      <rect
        className="fig-ch__body"
        height={BODY.h}
        rx={BODY.r}
        width={BODY.w}
        x={BODY.x}
        y={BODY.y}
      />
      <rect
        className="fig-ch__inner"
        height={INNER.h}
        rx={INNER.r}
        width={INNER.w}
        x={INNER.x}
        y={INNER.y}
      />
      <g className="fig-ch__mark">
        <ChamberMark hatchId={hatchId} state={state} />
      </g>
      <text className="fig-ch__well" textAnchor="middle" x={LABEL_X} y={LABEL_Y}>
        {well}
      </text>
    </svg>
  )
}
